import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiRequestError, SyncJob, SyncJobsList } from '@/lib/api-client';
import { syncJobsQuerySchema } from '@/lib/api-types';
import type { SyncJobsQueryInput } from '@/lib/api-types';
import { queryKeys } from '@/lib/query-keys';

export function useJobs(query: SyncJobsQueryInput = {}, enabled = true) {
  const parsedQuery = syncJobsQuerySchema.parse(query);

  return useQuery<SyncJobsList, ApiRequestError>({
    queryKey: queryKeys.jobs.list(parsedQuery),
    queryFn: () => apiClient.jobs.list(parsedQuery),
    placeholderData: keepPreviousData,
    enabled,
    refetchInterval: (queryState) =>
      queryState.state.data?.some((job) => job.status === 'RUNNING' || job.status === 'RETRYING')
        ? 10 * 1_000
        : false,
  });
}

export function useRetryJob() {
  const queryClient = useQueryClient();

  return useMutation<SyncJob, ApiRequestError, string>({
    mutationFn: apiClient.jobs.retry,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all() }),
      ]);
    },
  });
}
